import { useState } from 'react';
import clsx from 'clsx';
import {
  addDays,
  addMonths,
  addWeeks,
  endOfMonth,
  endOfWeek,
  format,
  isSameDay,
  isSameMonth,
  isToday,
  nextSaturday,
  nextMonday,
  startOfDay,
  startOfMonth,
  startOfWeek,
  subMonths,
  isWithinInterval,
} from 'date-fns';
import { ChevronLeft, ChevronRight, X } from '@/components/ui/icons';

interface Quick {
  label: string;
  date: () => Date;
}

const QUICK: Quick[] = [
  { label: 'Today', date: () => startOfDay(new Date()) },
  { label: 'Later', date: () => new Date(Date.now() + 3 * 3600e3) },
  { label: 'Tomorrow', date: () => startOfDay(addDays(new Date(), 1)) },
  { label: 'This weekend', date: () => startOfDay(nextSaturday(new Date())) },
  { label: 'Next week', date: () => startOfDay(nextMonday(new Date())) },
  { label: 'Next weekend', date: () => startOfDay(addWeeks(nextSaturday(new Date()), 1)) },
  { label: '2 weeks', date: () => startOfDay(addWeeks(new Date(), 2)) },
  { label: '4 weeks', date: () => startOfDay(addWeeks(new Date(), 4)) },
];

function quickHint(q: Quick) {
  const d = q.date();
  if (q.label === 'Later') return format(d, 'h:mm a');
  return format(d, q.label.includes('week') && !q.label.includes('end') && q.label !== 'Next week' ? 'd MMM' : 'EEE');
}

function MonthGrid({
  month,
  setMonth,
  start,
  due,
  onPick,
}: {
  month: Date;
  setMonth: (d: Date) => void;
  start: number | null;
  due: number | null;
  onPick: (d: Date) => void;
}) {
  const gridStart = startOfWeek(startOfMonth(month));
  const gridEnd = endOfWeek(endOfMonth(month));
  const days: Date[] = [];
  for (let d = gridStart; d <= gridEnd; d = addDays(d, 1)) days.push(d);
  const inRange = (d: Date) => start !== null && due !== null && start < due && isWithinInterval(d, { start: startOfDay(start), end: startOfDay(due) });
  return (
    <div className="dp-month">
      <div className="dp-month-head">
        <span className="dp-month-title">{format(month, 'MMMM yyyy')}</span>
        <button className="btn sm" onClick={() => setMonth(startOfMonth(new Date()))}>
          Today
        </button>
        <button className="icon-btn sm" onClick={() => setMonth(subMonths(month, 1))}>
          <ChevronLeft size={16} />
        </button>
        <button className="icon-btn sm" onClick={() => setMonth(addMonths(month, 1))}>
          <ChevronRight size={16} />
        </button>
      </div>
      <div className="dp-grid">
        {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map((d) => (
          <span key={d} className="dp-dow">
            {d}
          </span>
        ))}
        {days.map((d) => (
          <button
            key={d.toISOString()}
            className={clsx('dp-day', {
              out: !isSameMonth(d, month),
              today: isToday(d),
              selected: (due !== null && isSameDay(d, due)) || (start !== null && isSameDay(d, start)),
              range: inRange(d),
            })}
            onClick={() => onPick(d)}
          >
            {format(d, 'd')}
          </button>
        ))}
      </div>
    </div>
  );
}

/** Start/due date range picker used for task dates. */
export function DateRangeMenu({
  start,
  due,
  initialFocus = 'due',
  onChange,
  onClose,
}: {
  start: number | null;
  due: number | null;
  initialFocus?: 'start' | 'due';
  onChange: (start: number | null, due: number | null) => void;
  onClose?: () => void;
}) {
  const [focus, setFocus] = useState<'start' | 'due'>(initialFocus);
  const [month, setMonth] = useState(startOfMonth(new Date((focus === 'due' ? due : start) ?? Date.now())));
  const pick = (d: Date) => {
    const ts = d.getTime();
    if (focus === 'start') {
      onChange(ts, due !== null && due < ts ? ts : due);
      setFocus('due');
    } else {
      onChange(start !== null && start > ts ? ts : start, ts);
      onClose?.();
    }
  };
  return (
    <div className="dp" style={{ width: 520 }}>
      <div className="dp-inputs">
        {(['start', 'due'] as const).map((k) => {
          const v = k === 'start' ? start : due;
          return (
            <button key={k} className={clsx('dp-input', { focused: focus === k })} onClick={() => setFocus(k)}>
              <span className={clsx('truncate', { faint: !v })}>{v ? format(v, 'M/d/yy') : k === 'start' ? 'Start date' : 'Due date'}</span>
              {v && (
                <span
                  className="icon-btn xs"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (k === 'start') onChange(null, due);
                    else onChange(start, null);
                  }}
                >
                  <X size={12} />
                </span>
              )}
            </button>
          );
        })}
      </div>
      <div className="dp-body">
        <div className="dp-quick">
          {QUICK.map((q) => (
            <button key={q.label} className="dp-quick-item" onClick={() => pick(q.date())}>
              <span>{q.label}</span>
              <span className="faint">{quickHint(q)}</span>
            </button>
          ))}
        </div>
        <MonthGrid month={month} setMonth={setMonth} start={start} due={due} onPick={pick} />
      </div>
    </div>
  );
}

/** Single date picker (custom date fields, filters). */
export function DateMenu({ value, onChange, onClose }: { value: number | null; onChange: (v: number | null) => void; onClose?: () => void }) {
  const [month, setMonth] = useState(startOfMonth(new Date(value ?? Date.now())));
  const pick = (d: Date) => {
    onChange(d.getTime());
    onClose?.();
  };
  return (
    <div className="dp" style={{ width: 480 }}>
      <div className="dp-body">
        <div className="dp-quick">
          {QUICK.map((q) => (
            <button key={q.label} className="dp-quick-item" onClick={() => pick(q.date())}>
              <span>{q.label}</span>
              <span className="faint">{quickHint(q)}</span>
            </button>
          ))}
          {value !== null && (
            <button
              className="dp-quick-item danger"
              onClick={() => {
                onChange(null);
                onClose?.();
              }}
            >
              <span>Clear</span>
            </button>
          )}
        </div>
        <MonthGrid month={month} setMonth={setMonth} start={null} due={value} onPick={pick} />
      </div>
    </div>
  );
}
